import { isDefined, makeValuesPredicate } from '@sgme/fp';
import type { ClientOrRelativesRequest, ClientOrRelativesResponse } from 'apiConnector/secondary-market/shared/clients';
import type { ReloadFormResponse } from 'apiConnector/secondary-market/shared/form';
import type { ReferentialFormResponse } from 'apiConnector/secondary-market/shared/referential';
import type { Custodian } from 'models/custodian';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { Action } from 'state/actions';
import { getOrderById } from 'state/blotters/secondaryMarketOrderBlotter/secondaryMarketOrderBlotter.selector';
import { getOrderStatus, type LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import { limitOrderOrderActions } from 'state/secondaryMarket/limit-order/order/order.action';
import { type LiveSelectors, liveSelectors } from 'state/secondaryMarket/live/live.selector';
import { secondaryMarketLiveQuoteCancelRequested } from 'state/secondaryMarket/live/quote/liveQuote.action';
import { getLiveQuoteQuoteId } from 'state/secondaryMarket/live/quote/liveQuote.selector';
import {
  getSecondaryMarketProductClientFromRowId,
  getSecondaryMarketProductCustodianFromRowId,
  getSecondaryMarketProductTabId,
} from 'state/secondaryMarket/productSearch/productSearch.selectors';
import type { SecondaryOrderStatePath, TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import type { OrderActionType } from 'state/secondaryMarket/shared/actions';
import { secondaryMarketActionsFactory } from 'state/secondaryMarket/shared/actions';
import { secondaryMarketAmendActionsFactory } from 'state/secondaryMarket/shared/amend/amend.actions';
import { defaultsSelectorsFactory } from 'state/secondaryMarket/shared/defaults/defaults.selector';
import type { ClientType } from 'state/secondaryMarket/shared/form.models';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import { secondaryMarketInputFormActionsFactory } from 'state/secondaryMarket/shared/form/input/input.actions';
import { formInputSelectorsFactory } from 'state/secondaryMarket/shared/form/input/input.selector';
import { secondaryMarketOriginalFormActionsFactory } from 'state/secondaryMarket/shared/form/original/original.actions';
import { secondaryMarketKycActionsFactory } from 'state/secondaryMarket/shared/kyc/kyc.action';
import { secondaryMarketKycStatusThunksFactory } from 'state/secondaryMarket/shared/kyc/kyc.thunks';
import { Settlement } from 'state/secondaryMarket/shared/referential/referential.model';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import { fromSecondaryPostEventsWorkspace, fromSecondaryQuotesWorkspace } from 'state/selectors';
import type { Thunk, ThunkDispatch } from 'state/state.common';
import { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType, TabsForWorkspace } from 'state/workspaces/model';
import { secondaryMarketQuoteWorkspaceAddTab } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.actions';
import { getProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { workspaceTabCreated, workspaceTabSetBusy, workspaceTabUnfrozen } from 'state/workspaces/workspace.actions';
import { getTabWorkspaceType } from 'state/workspaces/workspaces/workspaces.selector';
import type { HttpError } from 'utils/http/httpClient.model';

const isServerError = makeValuesPredicate([500, 502, 504]);

export function secondaryMarketThunksFactory<
  AmendRequest,
  AmendResponse,
  Referential extends ReferentialFormResponse,
  ReloadFormRequest,
  PersistedTab,
>(
  actionType: Exclude<OrderActionType, 'CP_ORDER'>,
  stateType: Exclude<SecondaryOrderStatePath, 'cpOrder'>,
  secondaryMarketSelectors: LimitOrderSelectors | LiveSelectors,
) {
  const defaultSelectors = defaultsSelectorsFactory(stateType);
  const formInputSelectors = formInputSelectorsFactory(stateType, {});
  const amendActions = secondaryMarketAmendActionsFactory<AmendRequest, AmendResponse, typeof actionType>(actionType);
  const kycActions = secondaryMarketKycActionsFactory<typeof actionType>(actionType);
  const kycThunks = secondaryMarketKycStatusThunksFactory(stateType, actionType);
  const inputFormActions = secondaryMarketInputFormActionsFactory<
    Form,
    typeof actionType,
    ReloadFormRequest,
    Referential,
    PersistedTab
  >(actionType);
  const originalFormActions = secondaryMarketOriginalFormActionsFactory(actionType);
  const secondaryMarketActions = secondaryMarketActionsFactory<Referential, typeof actionType>(actionType);

  const resetSidePanelIfNeeded = (dispatch: ThunkDispatch, getState: () => AppState, tabId: string) => {
    const state = getState();
    const status = stateType === 'live' ? liveSelectors.getStatus(state, tabId) : getOrderStatus(state, tabId);
    const isRejected =
      status === 'QUOTE_FAILED' ||
      status === 'QUOTE_REJECTED' ||
      status === 'TRADE_FAILED' ||
      status === 'TRADE_REJECTED' ||
      status === 'ORDER_FAILED' ||
      status === 'ORDER_REJECTED';

    const workspaceType = getTabWorkspaceType(state, tabId)! as SecondaryWorkspaceType;
    const shouldResetSidePanel = isRejected;
    if (shouldResetSidePanel && stateType === 'live') {
      const quoteId = getLiveQuoteQuoteId(state, tabId)!;
      const productKey = getProductKey(state, tabId);
      dispatch(secondaryMarketLiveQuoteCancelRequested(tabId, quoteId, productKey));
    }
    if (shouldResetSidePanel && stateType === 'limitOrder') {
      dispatch(limitOrderOrderActions.secondaryMarketOrderCanceled(tabId, workspaceType));
    }
  };

  const initFormFetchedThunk = (
    tabId: string,
    response: ReloadFormResponse<Referential>,
    custodian?: Custodian,
  ): Thunk<void> => {
    return dispatch => {
      dispatch(secondaryMarketActions.initFormFetched(tabId, response));
      if (custodian) {
        dispatch(inputFormActions.formFieldChanged(tabId, 'custodian', custodian) as Action);
      }
      if (response.input?.salesId !== undefined) {
        dispatch(kycThunks.secondaryMarketKycStatusRequestedThunk(tabId));
      }
    };
  };
  const linkedToClientReceivedThunk = (tabId: string, response: ClientOrRelativesResponse): Thunk<void> => {
    return (dispatch, getState) => {
      const state = getState();

      dispatch(inputFormActions.clientReceived(tabId, response));

      const oldSalesId = secondaryMarketSelectors.getFormSalesId(state, tabId);

      if (response.selectedSale?.id !== undefined && response.selectedSale?.id !== oldSalesId) {
        dispatch(kycThunks.secondaryMarketKycStatusRequestedThunk(tabId));
      }
    };
  };
  const saleSelectedThunk = (tabId: string, salesId: string | undefined): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'salesId', salesId) as Action);
      dispatch(kycThunks.secondaryMarketKycStatusRequestedThunk(tabId));
    };
  };
  const saleDeskSelectedThunk = (tabId: string, salesDesk: string | undefined): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'salesId', undefined) as Action);
      dispatch(inputFormActions.formFieldChanged(tabId, 'salesDesk', salesDesk) as Action);
      dispatch(kycActions.secondaryMarketKycStatusReset(tabId));
    };
  };
  const resetClientData = (dispatch: ThunkDispatch, tabId: string) => {
    dispatch(inputFormActions.formFieldChanged(tabId, 'salesId', undefined) as Action);
    dispatch(inputFormActions.formFieldChanged(tabId, 'settlementType', undefined) as Action);
    dispatch(kycActions.secondaryMarketKycStatusReset(tabId));
  };
  const amendFailedThunk = (tabId: string, error?: HttpError): Thunk<void> => {
    return (dispatch, _) => {
      const errorStatus = error?.status ?? 500;
      const payload = error?.payload;

      const message =
        payload?.message !== undefined ? payload.message : typeof payload !== 'string' ? undefined : payload;

      if (isServerError(errorStatus)) {
        dispatch(amendActions.secondaryMarketAmendFailed(tabId, message));
      } else if (errorStatus === 412) {
        dispatch(amendActions.secondaryMarketAmendCollided(tabId, message));
      } else {
        dispatch(amendActions.secondaryMarketAmendRejected(tabId, message));
      }
      dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketPostEvents', false));
    };
  };
  const amendReceivedThunk = (tabId: string, data: AmendResponse): Thunk<void> => {
    return (dispatch, _) => {
      dispatch(amendActions.secondaryMarketAmendReceived(tabId, data));
      dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketPostEvents', false));
    };
  };
  const amendResetTabThunk = (tabId: string): Thunk<void> => {
    return (dispatch, getState) => {
      const state = getState();
      const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
      if (orderId === undefined) {
        return;
      }
      dispatch(workspaceTabUnfrozen(tabId, 'secondaryMarketPostEvents', false));
      dispatch(inputFormActions.resetForm(tabId));
      dispatch(originalFormActions.secondaryMarketAmendFetchRequested(tabId, orderId));
    };
  };
  const resetTabThunk = (tabId: string): Thunk<void> => {
    return (dispatch, getState) => {
      const state = getState();
      const bcsKey = fromSecondaryQuotesWorkspace.getProductKey(state, tabId);
      const bcsProductKey = toBcsProductKey(bcsKey);
      const tradeWay = defaultSelectors.getDefaultTradeWay(state, tabId);

      dispatch(inputFormActions.resetForm(tabId));
      if (bcsProductKey !== undefined) {
        dispatch(secondaryMarketActions.initFormFetchRequested(tabId, bcsProductKey, tradeWay) as Action);
      }
    };
  };
  const reloadFormFetchedThunk = (
    tabId: string,
    workspace: SecondaryWorkspaceType,
    response: ReloadFormResponse<Referential>,
  ): Thunk<void> => {
    return dispatch => {
      dispatch(inputFormActions.reloadFormFetched(tabId, workspace, response) as Action);
    };
  };

  const clientSelectedThunk = (
    tabId: string,
    workspace: SecondaryWorkspaceType,
    client: ISalesCounterpart | undefined,
  ): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      const state = getState();
      const key =
        workspace === 'secondaryMarketQuotes'
          ? toBcsProductKey(fromSecondaryQuotesWorkspace.getProductKey(state, tabId))
          : fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'client', client) as Action);
      dispatch(inputFormActions.formFieldChanged(tabId, 'clientType', 'Other') as Action);
      resetClientData(dispatch, tabId);

      if (client) {
        const request: ClientOrRelativesRequest = {
          bcsProductKey: key,
          client: client,
          clientType: 'Other',
          salesId: secondaryMarketSelectors.getFormSalesId(state, tabId),
          custodianBdrId: secondaryMarketSelectors.getFormCustodianBdrId(state, tabId),
        };
        dispatch(inputFormActions.clientRequested(tabId, request));
        if (isDefined(key)) {
          dispatch(inputFormActions.settlementDateRequested(tabId, key, request.client?.eliotCode, request.clientType));
        }
      }
    };
  };
  const custodianSelectedThunk = (
    tabId: string,
    workspace: SecondaryWorkspaceType,
    custodian?: Custodian | null,
  ): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      const state = getState();
      const key =
        workspace === 'secondaryMarketQuotes'
          ? toBcsProductKey(fromSecondaryQuotesWorkspace.getProductKey(state, tabId))
          : fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
      const client = secondaryMarketSelectors.getFormClient(state, tabId);
      const clientType = secondaryMarketSelectors.getFormClientType(state, tabId);

      dispatch(inputFormActions.formFieldChanged(tabId, 'custodian', custodian ?? undefined) as Action);
      if (key !== undefined && clientType === 'Other' && !!client?.id && !!custodian?.id) {
        dispatch(inputFormActions.custodianRequested(tabId, key, client, custodian.id));
      }
    };
  };

  const clientTypeSelectedThunk = (
    tabId: string,
    workspace: SecondaryWorkspaceType,
    clientType: ClientType,
  ): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      const state = getState();
      const key =
        workspace === 'secondaryMarketQuotes'
          ? toBcsProductKey(fromSecondaryQuotesWorkspace.getProductKey(state, tabId))
          : fromSecondaryPostEventsWorkspace.getBcsProductKey(state, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'clientType', clientType) as Action);
      dispatch(inputFormActions.formFieldChanged(tabId, 'client', undefined) as Action);

      resetClientData(dispatch, tabId);
      const request = {
        bcsProductKey: key!,
        clientType,
      } as ClientOrRelativesRequest;
      dispatch(inputFormActions.clientRequested(tabId, request));
      if (isDefined(key)) {
        dispatch(inputFormActions.settlementDateRequested(tabId, key, undefined, request.clientType));
      }
    };
  };

  const defaultQuantityFromCashAmountThunk = (tabId: string, cashAmount?: number): Thunk<void> => {
    return (dispatch, getState) => {
      const state = getState();
      const perUnit = secondaryMarketSelectors.getCashAmountPerQuantityUnit(state, tabId);
      if (cashAmount === undefined || perUnit === undefined) {
        return;
      }
      const defaultQuantity = Math.round(cashAmount / perUnit);
      dispatch(inputFormActions.formFieldChanged(tabId, 'quantity', defaultQuantity) as Action);
    };
  };
  const cashAmountChangedThunk = (tabId: string, cashAmount?: number): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'cashAmount', cashAmount) as Action);
      dispatch(inputFormActions.formFieldChanged(tabId, 'tradeSizeInputMode', 'amount') as Action);
      if (cashAmount === undefined) {
        dispatch(inputFormActions.formFieldChanged(tabId, 'quantity', undefined) as Action);
        return;
      }
      dispatch(defaultQuantityFromCashAmountThunk(tabId, cashAmount));
    };
  };

  const settlementTypeChangedThunk = (tabId: string, value?: Settlement): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'settlementType', value) as Action);
    };
  };

  const cashAmountResetThunk = (tabId: string): Thunk<void> => {
    return (dispatch, _) => {
      dispatch(inputFormActions.formFieldChanged(tabId, 'tradeSizeInputMode', 'amount') as Action);
    };
  };

  const defaultCashAmountFromQuantityThunk = (tabId: string, quantity?: number): Thunk<void> => {
    return (dispatch, getState) => {
      const state = getState();
      const cashAmount = secondaryMarketSelectors.getCashAmountPerQuantityUnit(state, tabId);
      if (quantity === undefined || cashAmount === undefined) {
        return;
      }

      const defaultCashAmount = cashAmount * quantity;
      dispatch(inputFormActions.formFieldChanged(tabId, 'cashAmount', defaultCashAmount) as Action);
    };
  };

  const quantityChangedThunk = (tabId: string, quantity?: number): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'tradeSizeInputMode', 'quantity') as Action);
      dispatch(inputFormActions.formFieldChanged(tabId, 'quantity', quantity) as Action);
      if (quantity === undefined) {
        dispatch(inputFormActions.formFieldChanged(tabId, 'cashAmount', undefined) as Action);
        return;
      }
      dispatch(defaultCashAmountFromQuantityThunk(tabId, quantity));
    };
  };
  const quantityResetThunk = (tabId: string): Thunk<void> => {
    return (dispatch, _) => {
      dispatch(inputFormActions.formFieldChanged(tabId, 'tradeSizeInputMode', 'quantity') as Action);
    };
  };
  const tradeSizeInputModeChangedThunk = (tabId: string): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      const state = getState();
      const mode = formInputSelectors.getFormTradeSizeInputMode(state, tabId);
      dispatch(
        inputFormActions.formFieldChanged(
          tabId,
          'tradeSizeInputMode',
          mode === 'quantity' ? 'amount' : 'quantity',
        ) as Action,
      );
    };
  };
  const tradeWayChangedThunk = (tabId: string, value: TradeWay): Thunk<void> => {
    return (dispatch, getState) => {
      resetSidePanelIfNeeded(dispatch, getState, tabId);
      dispatch(inputFormActions.formFieldChanged(tabId, 'tradeWay', value) as Action);
      const state = getState();
      const mode = formInputSelectors.getFormTradeSizeInputMode(state, tabId);
      const quantity = secondaryMarketSelectors.getFormQuantity(state, tabId);
      const cashAmount = secondaryMarketSelectors.getFormCashAmount(state, tabId);
      if (mode === 'amount' && cashAmount !== undefined) {
        dispatch(defaultQuantityFromCashAmountThunk(tabId, cashAmount));
      }
      if (mode === 'quantity' && quantity !== undefined) {
        dispatch(defaultCashAmountFromQuantityThunk(tabId, quantity));
      }
    };
  };

  return {
    amendFailedThunk,
    amendReceivedThunk,
    amendResetTabThunk,
    resetTabThunk,
    reloadFormFetchedThunk,
    initFormFetchedThunk,
    linkedToClientReceivedThunk,
    clientSelectedThunk,
    custodianSelectedThunk,
    saleSelectedThunk,
    saleDeskSelectedThunk,
    clientTypeSelectedThunk,
    defaultQuantityFromCashAmountThunk,
    cashAmountChangedThunk,
    tradeSizeInputModeChangedThunk,
    cashAmountResetThunk,
    defaultCashAmountFromQuantityThunk,
    quantityChangedThunk,
    quantityResetThunk,
    tradeWayChangedThunk,
    settlementTypeChangedThunk,
    resetSidePanelIfNeeded,
  };
}

export function tabCreatedThunksFactory(
  actionType: Exclude<OrderActionType, 'CP_ORDER'>,
  workspaceTabType: TabsForWorkspace['secondaryMarketQuotes'],
  getTabName: (id: string) => string,
) {
  const secondaryMarketActions = secondaryMarketActionsFactory<any, typeof actionType>(actionType);
  const tabCreatedThunk =
    (bcsKey: string, tradeWay?: TradeWay): Thunk<void> =>
    (dispatch, getState, { getNewGuid }) => {
      const state = getState();
      const tabId = getNewGuid();
      const bcsProductKey = toBcsProductKey(bcsKey);
      const id = getSecondaryMarketProductTabId(state, bcsKey)!;
      const client = getSecondaryMarketProductClientFromRowId(state, bcsKey);
      const custodian = getSecondaryMarketProductCustodianFromRowId(state, bcsKey);

      dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, bcsKey, workspaceTabType));
      dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', workspaceTabType, getTabName(id)));
      if (bcsProductKey !== undefined) {
        dispatch(
          secondaryMarketActions.initFormFetchRequested(tabId, bcsProductKey, tradeWay, client, custodian) as Action,
        );
      }
    };

  const tabCreatedByOderIdThunk =
    (orderId: string): Thunk<void> =>
    (dispatch, getState, { getNewGuid }) => {
      const state = getState();
      const tabId = getNewGuid();
      const order = getOrderById(state, orderId);

      if (order === undefined) {
        return;
      }
      const bcsKey = `${order.productIdSource}:${order.productIdType}:${order.productIdValue}`;
      const bcsProductKey = toBcsProductKey(bcsKey);
      const tabNameId = order.isin ?? order.fileCode;
      dispatch(secondaryMarketQuoteWorkspaceAddTab(tabId, bcsKey, workspaceTabType));
      dispatch(workspaceTabCreated(tabId, 'secondaryMarketQuotes', workspaceTabType, getTabName(tabNameId)));
      if (bcsProductKey !== undefined) {
        dispatch(secondaryMarketActions.initFormFetchRequested(tabId, bcsProductKey) as Action);
      }
    };

  return {
    tabCreatedThunk,
    tabCreatedByOderIdThunk,
  };
}

export type SecondaryMarketTabCreatedThunks = ReturnType<typeof tabCreatedThunksFactory>;
